/**
 * Pure helpers for `AppConfig` — extracted so the test mock at
 * `@/test/mocks/app-config.ts` can reuse them without duplicating 35 lines.
 */
import type {TypeValue} from '@/types/types'
import type {AttributeDefinition} from '@/types/attribute-definition'
import type {PartyRelationshipSchemaEntry} from '@/types/party'

export interface PaginationConfig {
    defaultPageSize: number
    pageSizeOptions: number[]
}

export type LocalizedTypeValue = TypeValue & { label: string }

export interface AppConfig {
    accessProfileBindingOperationTypes: LocalizedTypeValue[]
    accessProfileStatuses: LocalizedTypeValue[]
    accountStatuses: LocalizedTypeValue[]
    assignmentStatuses: LocalizedTypeValue[]
    connectorTypes: LocalizedTypeValue[]
    dataClassificationTypes: LocalizedTypeValue[]
    domainEntityTypes: LocalizedTypeValue[]
    enforcementTypes: LocalizedTypeValue[]
    environmentTypes: LocalizedTypeValue[]
    functionGroupTypes: LocalizedTypeValue[]
    gracePeriodDays: number[]
    identifierTypes: LocalizedTypeValue[]
    identityTypes: LocalizedTypeValue[]
    lifecycleStates: LocalizedTypeValue[]
    partyRelationshipTypes: LocalizedTypeValue[]
    partyTypes: LocalizedTypeValue[]
    resourceResponsibilityTypes: LocalizedTypeValue[]
    resourceTypes: LocalizedTypeValue[]
    riskLevelTypes: LocalizedTypeValue[]
    systemTypes: LocalizedTypeValue[]
    targetSystemResponsibilityTypes: LocalizedTypeValue[]
    accessProfileResponsibilityTypes: LocalizedTypeValue[]
    functionGroupResponsibilityTypes: LocalizedTypeValue[]
    restrictionRuleResponsibilityTypes: LocalizedTypeValue[]
    autoAssignRuleResponsibilityTypes: LocalizedTypeValue[]
    certificationPolicyResponsibilityTypes: LocalizedTypeValue[]
    certificationCampaignResponsibilityTypes: LocalizedTypeValue[]
    workflowSteps: LocalizedTypeValue[]
    paginationConfig: PaginationConfig
    supportedLocales: string[]
    defaultLocale: string
    identityAttributeDefinitions: AttributeDefinition[]
    partyAttributeDefinitions: AttributeDefinition[]
    resourceAttributeDefinitions: AttributeDefinition[]
    functionGroupAttributeDefinitions: AttributeDefinition[]
    targetSystemAttributeDefinitions: AttributeDefinition[]
    partyRelationshipSchema: PartyRelationshipSchemaEntry[]
    typeValuesByName: Record<string, LocalizedTypeValue[]>
    /**
     * Threshold above which ENUM-style filter pickers in `<UnifiedSearchBar>`
     * switch from inline checkbox lists to a Combobox-with-chips.
     * Default 5 (set by backend AppConfigController). Issue 342.15.
     */
    filterPickerInlineThreshold: number
    /**
     * Threshold above which the popover-anchored {@link MultiPickerPopover}
     * renders an inner search input. Lists at or below this count render
     * the picker without a search box (visual scan beats typing); above it,
     * the search input appears so longer lists stay usable.
     * Default 10 (set by backend AppConfigController). Issue 401.
     */
    filterPickerSearchThreshold: number
    devMode: boolean
    timeSwitchEnabled: boolean
    /**
     * Backend build identifiers surfaced to the UI footer (issues 632 + 650).
     * Sourced from the maven `info.app.*` resource-filtered properties; the
     * controller falls back to `'dev'` / `''` when the tokens are unfilled.
     */
    backendVersion: string
    backendGitTag: string
    backendGitBranch: string
}

export const DEFAULT_PAGINATION_CONFIG: PaginationConfig = {
    defaultPageSize: 25,
    pageSizeOptions: [10, 25, 50, 100],
}

export const EMPTY_CONFIG: AppConfig = {
    accessProfileBindingOperationTypes: [],
    accessProfileStatuses: [],
    accountStatuses: [],
    assignmentStatuses: [],
    connectorTypes: [],
    dataClassificationTypes: [],
    domainEntityTypes: [],
    enforcementTypes: [],
    environmentTypes: [],
    functionGroupTypes: [],
    gracePeriodDays: [],
    identifierTypes: [],
    identityTypes: [],
    lifecycleStates: [],
    partyRelationshipTypes: [],
    partyTypes: [],
    resourceResponsibilityTypes: [],
    resourceTypes: [],
    riskLevelTypes: [],
    systemTypes: [],
    targetSystemResponsibilityTypes: [],
    accessProfileResponsibilityTypes: [],
    functionGroupResponsibilityTypes: [],
    restrictionRuleResponsibilityTypes: [],
    autoAssignRuleResponsibilityTypes: [],
    certificationPolicyResponsibilityTypes: [],
    certificationCampaignResponsibilityTypes: [],
    workflowSteps: [],
    paginationConfig: DEFAULT_PAGINATION_CONFIG,
    supportedLocales: ['en'],
    defaultLocale: 'en',
    identityAttributeDefinitions: [],
    partyAttributeDefinitions: [],
    resourceAttributeDefinitions: [],
    functionGroupAttributeDefinitions: [],
    targetSystemAttributeDefinitions: [],
    partyRelationshipSchema: [],
    typeValuesByName: {},
    /** Issue 342.27 — default lowered from 5 to 3; tiny ENUM lists render inline. */
    filterPickerInlineThreshold: 3,
    /** Issue 401 — default 10; only above this count does MultiPickerPopover render its inner search input. */
    filterPickerSearchThreshold: 10,
    devMode: false,
    timeSwitchEnabled: false,
    backendVersion: 'dev',
    backendGitTag: '',
    backendGitBranch: '',
}

export function resolveLabel(tv: TypeValue, lang: string): string {
    return tv.translations?.[lang]?.label ?? tv.translations?.en?.label ?? tv.code
}

export function localizeTypeValues(values: TypeValue[], lang: string): LocalizedTypeValue[] {
    return values.map(tv => ({...tv, label: resolveLabel(tv, lang)}))
}

export function labelOf(values: TypeValue[] | LocalizedTypeValue[], code: string): string {
    const value = values.find(v => v.code === code)
    if (!value) return code
    if ('label' in value) return (value as LocalizedTypeValue).label
    return value.translations?.en?.label ?? code
}

export function codesOf(values: TypeValue[]): string[] {
    return values.map(v => v.code)
}
